/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { milestoneService, trackingService } from 'services';
import { userStore } from 'stores';

/**
 * Creates a new version.
 *
 * @param {{
 *     file: Object,
 *     name: String,
 *     description?: String,
 *     origin: String,
 *     organizationPublic: Boolean
 *     onSuccess?: Function
 * }}  The parameters for creating a new version.
 * The `file` parameter is the current resource.
 * The `name` parameter is the name of the version.
 * The `description` parameter is the description of the version.
 * The `origin` parameter is used for tracking purposes
 * The `organizationPublic` parameter is used for setting the version public to the organization.
 * The `onSuccess` parameter is a callback function that will be called after the version is created.
 */
export const createVersion = ({
  file,
  name,
  description = undefined,
  origin,
  organizationPublic = false,
  onSuccess = () => {}
}) => {
  const payload = {
    fileId: file.id,
    name,
    organizationPublic
  };

  if (description?.trim() !== '') {
    payload.description = description;
  }

  return milestoneService.create(payload).then((version) => {
    trackingService.trackCreateMilestone(origin, file, organizationPublic);
    onSuccess(version);
  });
};

/**
 * Creates a new version only if the user has the required roles.
 * @param {{
 *     file: Object,
 *     name: String,
 *     description?: String,
 *     origin: String,
 *     organizationPublic: Boolean
 *     onSuccess?: Function
 * }}  The parameters for creating a new version.
 * The `file` parameter is the current diagram.
 * The `name` parameter is the name of the version.
 * The `description` parameter is the description of the version.
 * The `append` parameter is used to set the given version as the latest one.
 * The `origin` parameter is used for tracking purposes
 * The `organizationPublic` parameter is used for setting the version public to the organization.
 * The `onSuccess` parameter is a callback function that will be called after the version is created.
 *
 * @returns {Promise<unknown>}
 */
export const createVersionWithRolesCheck = async ({
  file,
  name,
  description = undefined,
  origin,
  organizationPublic = false,
  onSuccess = () => {}
}) => {
  const hasRevokedRoles = await userStore.checkRevokedRoles();

  if (hasRevokedRoles) {
    return Promise.reject('Token was refreshed');
  }

  const payload = {
    file,
    name,
    origin,
    organizationPublic,
    onSuccess
  };

  if (description?.trim() !== '') {
    payload.description = description;
  }

  return createVersion(payload);
};
